// Envoy ledger export: one line per money event, the shape Finance totals in
// Excel (Madison 2026-09-02). Three activities per client at most:
//
//   SA signed    +$250   the day the client signed
//   Dead         -$250   the client withdrew before ever paying us (the $250
//                        was not earned); the inferred went-Dead date
//   Client paid  +$450   the client's first invoice was paid in full
//
// A client who paid us and later went to DEAD keeps the $250, so no Dead line.
// When a window is set, lines are kept by THEIR OWN date (what happened in
// the window), not by the client's signing date.
//
// Resurrections (Nic 2026-09-02): a client who went to DEAD and came back has
// one entry per closed cycle in meta.history. Each becomes a Dead -$250 on
// the day they went to DEAD and a Restored +$250 on the day they came back, so
// an old month's file never changes after the fact and the comeback lands
// in its own month. A recovery (Envoy returning the money) is cash, not an
// accrual, and stays off this export.
//
// VA.gov (Nic 2026-10-25): an agreement signed under the VA.gov rule (the row
// carries vaGov) is owed from the day their specialist confirmed VA.gov, so
// its +$250 line is "VA.gov confirmed" on that day instead of "SA signed"
// (that day decides the month it is paid in).

import dayjs from 'dayjs';

export const LEDGER_CSV_HEADER = [
  'Client',
  'Envoy Lead ID',
  'Activity',
  'Date',
  'Amount',
];

const UNEARNED_SA = new Set(['void', 'recoup', 'recouped']);

// Central days, like the dashboard (QA 2026-10-26): the browser's own zone
// put a late-evening line on the next day (or month) outside Central. A
// bare YYYY-MM-DD is already a day and stays as is.
const CENTRAL_DAY = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Chicago',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});
const ymd = (v) => {
  if (!v) return '';
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const d = dayjs(v);
  return d.isValid() ? CENTRAL_DAY.format(d.toDate()) : '';
};

export const buildEnvoyLedgerLines = (rows, range) => {
  const lines = [];
  for (const r of rows || []) {
    const client = r.clientName || '';
    const leadId = r.leadExternalId || '';
    const saAmount = Number(r.sa?.amount) || 0;
    const payAmount = Number(r.pay?.amount) || 0;

    // A 'pending' $250 (VA.gov rule, not confirmed yet, the client paid us
    // first; QA 2026-10-26) is not owed yet: no line until it is.
    if (r.sa?.status === 'pending') {
      // nothing for the $250 yet
    } else if (r.vaGov?.at) {
      lines.push({ client, leadId, activity: 'VA.gov confirmed', date: ymd(r.vaGov.at), amount: saAmount });
    } else if (r.saSignedAt) {
      lines.push({ client, leadId, activity: 'SA signed', date: ymd(r.saSignedAt), amount: saAmount });
    }
    const history = Array.isArray(r.meta?.history) ? r.meta.history : [];
    for (const h of history) {
      const deadAt = h.withdrawal?.at || h.voidedAt || h.recoupFlaggedAt;
      lines.push({ client, leadId, activity: 'Dead', date: ymd(deadAt), amount: -saAmount });
      lines.push({ client, leadId, activity: 'Restored', date: ymd(h.restoredAt), amount: saAmount });
    }
    if (UNEARNED_SA.has(r.sa?.status)) {
      const deadAt = r.meta?.withdrawal?.at || r.sa?.voidedAt || r.sa?.recoupFlaggedAt;
      lines.push({ client, leadId, activity: 'Dead', date: ymd(deadAt), amount: -saAmount });
    }
    if (r.pay?.clientFirstPaidAt) {
      lines.push({ client, leadId, activity: 'Client paid', date: ymd(r.pay.clientFirstPaidAt), amount: payAmount });
    }
  }

  const inRange = (line) => {
    if (!range?.start || !range?.end) return true;
    if (!line.date) return false;
    return line.date >= range.start && line.date <= range.end;
  };

  return lines
    .filter(inRange)
    .sort((a, b) => a.date.localeCompare(b.date) || a.client.localeCompare(b.client))
    .map((l) => [l.client, l.leadId, l.activity, l.date, l.amount]);
};
